import { useEffect, useRef, useState } from 'react';
import { RadioGroup } from '@platform/witems';
import { VISIT_KINDS, VISIT_KIND_NAME, VISIT_KIND_PERSON, isVisitKind } from '@/logic/visits';

// The one place a care-team member asks a patient to book a visit (patient-file top bar and header, the chat's
// quick action). Nothing is preselected: the kind decides the link, the card and who the visit is with.
const OPTIONS = VISIT_KINDS.map((kind) => ({
  value: kind,
  label: `${VISIT_KIND_PERSON[kind]} (${VISIT_KIND_NAME[kind]})`,
}));

/**
 * @param {{ patientName?: string | null, demoMode?: boolean, busy?: boolean, error?: string | null,
 *   returnFocusRef?: { current: HTMLElement | null } | null, onConfirm: (kind: string) => void, onClose: () => void }} props
 */
export default function RequestVisitModal({ patientName, demoMode = false, busy = false, error = null, returnFocusRef = null, onConfirm, onClose }) {
  const [kind, setKind] = useState('');
  const dialogRef = useRef(null);
  // A request in flight is never abandoned mid-send, whoever the caller is.
  const close = () => { if (!busy) onClose(); };
  // The latest close, so the listener below is attached once per open, not on every parent render.
  const closeRef = useRef(close);
  useEffect(() => { closeRef.current = close; });

  // CAPTURE + stopPropagation: the patient drawer's own Escape listener would navigate it closed.
  // Focus moves into the dialog, and back where the user was (or to the caller's choice) on close.
  useEffect(() => {
    const opener = document.activeElement;
    const returnTo = returnFocusRef?.current || null;
    const onKey = (e) => { if (e.key !== 'Escape') return; e.stopPropagation(); closeRef.current(); };
    window.addEventListener('keydown', onKey, true);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = prev;
      const back = returnTo || opener;
      if (back && typeof back.focus === 'function' && back.isConnected) back.focus();
    };
  }, [returnFocusRef]);

  const canSubmit = isVisitKind(kind) && !busy;

  return (
    <div className="modal-overlay" onClick={close}>
      <div ref={dialogRef} tabIndex={-1} className="modal-content" role="dialog" aria-modal="true" aria-labelledby="request-visit-title" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <h2 className="header3" id="request-visit-title">Request a visit</h2>
          <button className="modal-close" aria-label="Close" onClick={close} disabled={busy}>×</button>
        </header>
        <div className="modal-body">
          <p className="body2" id="request-visit-question" style={{ margin: '0 0 12px' }}>
            Who should <span className={demoMode ? 'demo-blur' : undefined}>{patientName || 'this patient'}</span> book a visit with?
          </p>
          <fieldset aria-labelledby="request-visit-question" style={{ border: 0, margin: 0, padding: 0 }}>
            <RadioGroup name="request-visit-kind" options={OPTIONS} value={kind} handleChange={(v) => setKind(String(v))} disabled={busy} />
          </fieldset>
          <p className="detail1 text-secondary" style={{ margin: '12px 0 0' }}>
            We&apos;ll send them that visit&apos;s booking link and add a booking task, unless they already have one to do.
          </p>
          {error && <div className="error-banner" style={{ marginTop: 12 }}>{error}</div>}
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={close} disabled={busy}>Cancel</button>
          <button className="btn-primary" onClick={() => canSubmit && onConfirm(kind)} disabled={!canSubmit}>
            {busy ? 'Sending…' : 'Send link'}
          </button>
        </div>
      </div>
    </div>
  );
}
